/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 共有 AppShell の Header に渡すユーザーメニュー。アバター + ドロップダウン
 *  （マイページ・設定へのリンク、ログアウト）を提供する。ルーティングに依存するため PIL 側に置く
 * @graph-connects none
 */
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { Avatar } from '@cortex/web-design-system/components/ui/Avatar';

interface UserMenuProps {
  /** ログイン中ユーザー情報 */
  user: {
    name: string;
    email: string;
    picture?: string;
  };
  /** ログアウト処理 */
  onLogout?: () => void;
}

/**
 * ヘッダーのユーザーメニュー。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business アバター + ユーザードロップダウン（マイページ・設定・ログアウト）
 * @graph-connects none
 */
export function UserMenu({ user, onLogout }: UserMenuProps): ReactNode {
  const { t } = useTranslation();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => {
          setIsUserMenuOpen(!isUserMenuOpen);
        }}
        className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-[var(--color-bg-hover)] transition-colors"
        aria-expanded={isUserMenuOpen}
        aria-haspopup="true"
      >
        <Avatar src={user.picture} name={user.name} size="sm" />
        <span className="hidden sm:block text-sm text-[var(--color-text-primary)]">
          {user.name}
        </span>
        <svg
          className={`w-4 h-4 text-[var(--color-text-muted)] transition-transform ${
            isUserMenuOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isUserMenuOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => {
              setIsUserMenuOpen(false);
            }}
            aria-hidden="true"
          />
          <div className="absolute right-0 mt-2 w-56 z-20 bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-xl shadow-xl py-1 animate-fade-in">
            <div className="px-4 py-3 border-b border-[var(--color-border)]">
              <p className="text-sm font-medium text-[var(--color-text-primary)]">{user.name}</p>
              <p className="text-xs text-[var(--color-text-muted)] truncate">{user.email}</p>
            </div>
            <Link
              to="/my-page"
              onClick={() => {
                setIsUserMenuOpen(false);
              }}
              className="flex items-center gap-2 w-full px-4 py-2 text-left text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              {t('nav.myPage')}
            </Link>
            <Link
              to="/settings"
              onClick={() => {
                setIsUserMenuOpen(false);
              }}
              className="flex items-center gap-2 w-full px-4 py-2 text-left text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              {t('nav.settings')}
            </Link>
            <button
              onClick={() => {
                setIsUserMenuOpen(false);
                onLogout?.();
              }}
              className="flex items-center gap-2 w-full px-4 py-2 text-left text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-error)] hover:bg-[var(--color-bg-hover)] transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              {t('auth.logout')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
